import { useContext, useEffect } from 'react'

import { useSetState } from '@domain-group/react-hooks'

import { generateApplicationUrl, launchModal } from '@domain-group/javascript-utils'

import useCampaign, { UseCampaignPayload, generateUrls } from '@hooks/useCampaign'
import useFlipperKey from '@hooks/useFlipperKey'
import useImage from '@hooks/useImage'
import useImages from '@hooks/useImages'
import useSelectEntities from '@hooks/useSelectEntities'

import { baseURL } from '@functions/api'

import PageContext from '@contexts/pageContext'

import type { UseImagePayload } from '@hooks/useImage'

import type { CampaignModel, ImageModel } from '@models/types'

export type GalleryView = 'largeGrid' | 'grid' | 'list'

const defaultState = {
  bulkSelecting: false,
  exportSizeErrorImageIds: [] as number[],
  view: 'grid' as GalleryView,
}

type SetState = (state: Partial<typeof defaultState>) => void

type CheckImageExportSizesParams = {
  checkImageExportSizesFn: UseCampaignPayload['callbacks']['checkImageExportSizes'],
  setState: SetState,
}

const checkImageExportSizes = (params: CheckImageExportSizesParams) => {
  const { checkImageExportSizesFn, setState } = params

  // @ts-expect-error TS2339
  checkImageExportSizesFn().then(({ success, data }) => {
    if (success) setState({ exportSizeErrorImageIds: data.image_ids })
  })
}

type SaveBulkSelectSort = {
  deselectAll: () => void,
  filteredImages: ImageModel[],
  selectedEntityIds: number[],
  setState: SetState,
  updateSortOrder: UseImagePayload['callbacks']['updateSortOrder'],
}

const saveBulkSelectSort = (params: SaveBulkSelectSort) => {
  const {
    deselectAll, filteredImages, selectedEntityIds, setState, updateSortOrder,
  } = params

  const unselectedImageIds = filteredImages.filter(image => !selectedEntityIds.includes(image.id)).map(i => i.id)

  const sortedIds = [...selectedEntityIds, ...unselectedImageIds]

  updateSortOrder(sortedIds).then(({ success }) => {
    if (success){
      setState({ bulkSelecting: false })
      deselectAll()
    }
  })
}

type BuildProofSheetLinkParams = {
  campaign: CampaignModel,
  selectedEntityIds: number[],
}

const buildProofSheetLink = (params: BuildProofSheetLinkParams) => {
  const { campaign, selectedEntityIds } = params

  if (selectedEntityIds.length){
    let url = `${baseURL}/campaigns/${campaign.id}/proof_sheet?`

    selectedEntityIds.forEach((imageId, index) => {
      url += `image_ids[]=${imageId}`
      if (index !== selectedEntityIds.length - 1) url += '&'
    })

    return url
  }

  return ''
}

type ResetExportSizeErrorsParams = {
  selectedEntityIds: number[],
  setState: SetState,
  state: Partial<typeof defaultState>,
}

const resetExportSizeErrors = (params: ResetExportSizeErrorsParams) => {
  const {
    selectedEntityIds, setState, state,
  } = params

  const { exportSizeErrorImageIds } = state

  setState({
    // @ts-expect-error TS18048
    exportSizeErrorImageIds: exportSizeErrorImageIds.filter(id => !selectedEntityIds.includes(id)),
  })
}

type BuildPublicMediaReviewerUrlParams = {
  campaign: CampaignModel,
  selectedImageIds: number[],
}

const buildPublicMediaReviewerUrl = (params: BuildPublicMediaReviewerUrlParams) => {
  const { selectedImageIds = [], campaign } = params

  const { public_token } = campaign || {}

  if (!public_token) return ''

  const { mediaReviewerPublicUrl } = generateUrls(campaign, { imageIds: selectedImageIds })

  // @ts-expect-error TS2339
  const { applicationUrlWithHash } = generateApplicationUrl({
    applicationKey: 'realhub',
    // @ts-expect-error TS2322
    environment: process.env.REACT_APP_APPLICATION_ENV,
    hash: mediaReviewerPublicUrl,
  })

  return applicationUrlWithHash
}

type SelectSpecifiedPortalImageIdsParams = {
  setSelectedIds: (state: number[]) => void,
  setState: SetState,
  specifiedPortalImageIds: number[],
}

const selectSpecifiedPortalImageIds = (params: SelectSpecifiedPortalImageIdsParams) => {
  const { setSelectedIds, setState, specifiedPortalImageIds } = params

  setSelectedIds(specifiedPortalImageIds)

  setState({ bulkSelecting: true })
}

const useCampaignImages = (campaignId: number) => {
  const [state, setState] = useSetState(defaultState)
  const {
    bulkSelecting, exportSizeErrorImageIds, view,
  } = state

  const isGridView = view !== 'list'

  const { isEnabled: photosPlansV2 } = useFlipperKey('photos_plans_v2')

  const {
    callbacks: {
      checkImageExportSizes: checkImageExportSizesFn,
      createPublicToken,
      downloadMedia,
    },
    campaign,
    campaignUniqueId,
    crmSyncApi,
    loaded,
  } = useCampaign({ id: campaignId })

  const imagesPayload = useImages({
    filters: {
      hidden: 'false',
      imageable_id: campaignId,
      imageable_type: 'Campaign',
    },
    performHttpRequests: !!campaign.cache_key,
  })

  const {
    callbacks: {
      apiExportImages, bulkDeleteImages, bulkUpdateImages, updateSortOrder,
    },
    deleting,
  } = useImage()

  const { public_token } = campaign

  const { callbacks } = useContext(PageContext)

  const { filteredImages, hasFilteredImages, loading } = imagesPayload

  const selectEntitiesPayload = useSelectEntities()
  const {
    callbacks: selectEntitiesCallbacks, hasSelectedEntities: hasSelectedImages, selectedEntityIds,
  } = selectEntitiesPayload

  const { deselectAll, selectAll, setSelectedIds } = selectEntitiesCallbacks

  useEffect(() => {
    checkImageExportSizes({ checkImageExportSizesFn, setState })
  }, [])

  useEffect(() => {
    if (campaignId && loaded && !public_token){
      createPublicToken()
    }
  }, [campaignId, loaded, public_token])

  const allSelected = selectedEntityIds.length === filteredImages.length
  const canExportImages = !!crmSyncApi && !!campaignUniqueId
  const hasExportSizeErrorImageIds = !!exportSizeErrorImageIds.length
  // @ts-expect-error TS2339
  const specifiedPortalImageIds = campaign?.data?.specified_portal_image_ids || []
  const hasSpecifiedPortalImages = !!specifiedPortalImageIds.length

  return {
    allSelected,
    bulkSelecting: photosPlansV2 ? false : bulkSelecting,
    callbacks: {
      ...selectEntitiesCallbacks,
      apiExportImages: () => apiExportImages(selectedEntityIds),
      buildProofSheetLink: () => buildProofSheetLink({ campaign, selectedEntityIds }),
      bulkDeleteImages: () => bulkDeleteImages(selectedEntityIds),
      bulkUpdateImages: (updatedParams: Partial<ImageModel>) => bulkUpdateImages(selectedEntityIds, updatedParams),
      copyLink: () => launchModal({
        callbacks,
        modalKey: 'CopyLinkModal',
        payload: { campaign, link: buildPublicMediaReviewerUrl({ campaign, selectedImageIds: selectedEntityIds }) },
      }),
      downloadMedia: () => downloadMedia(selectedEntityIds),
      resetExportSizeErrors: () => resetExportSizeErrors({ selectedEntityIds, setState, state }),
      saveBulkSelectSort: () => saveBulkSelectSort({
        deselectAll,
        filteredImages,
        selectedEntityIds,
        setState,
        updateSortOrder,
      }),
      selectAll: () => selectAll(filteredImages),
      setState,
      selectSpecifiedPortalImageIds: () => selectSpecifiedPortalImageIds({
        setSelectedIds,
        setState,
        specifiedPortalImageIds,
      }),
      setView: (nextView: GalleryView) => setState({ view: nextView }),
      toggleView: () => setState({ view: isGridView ? 'list' : 'grid' }),
      updateImageSort: updateSortOrder,
    },
    campaign,
    canExportImages,
    deleting,
    exportSizeErrorImageIds,
    filteredImages,
    hasExportSizeErrorImageIds,
    hasFilteredImages,
    hasPublicImages: filteredImages.some(image => !image.private),
    hasSelectedImages,
    hasSpecifiedPortalImages,
    isGridView,
    loading,
    publicImages: filteredImages.filter(image => !image.private),
    selectedEntityIds,
    view,
  }
}

export type UseCampaignImagesPayload = ReturnType<typeof useCampaignImages>

export default useCampaignImages
